/**
 * Unified Device Detection System
 * 
 * This module provides a single source of truth for device detection,
 * using robust feature detection rather than device-specific targeting.
 */

import React from 'react';

export interface DeviceInfo {
  type: 'iphone' | 'ipad' | 'android' | 'desktop' | 'unknown';
  isIOS: boolean;
  isPWA: boolean;
  hasNotch: boolean;
  hasDynamicIsland: boolean;
  hasHomeIndicator: boolean;
  isFoldCover: boolean;
  screenSize: {
    width: number;
    height: number;
  };
  deviceClass: 'compact' | 'regular' | 'large';
  safeAreaSupport: boolean;
}

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Detects device capabilities using feature detection
 */
export function getDeviceInfo(): DeviceInfo {
  const userAgent = navigator.userAgent;
  const { width, height } = window.screen;
  
  // Basic device type detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window);
  const isIPhone = /iPhone/.test(userAgent);
  const isIPad = /iPad/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  // `navigator.standalone` is a non-standard iOS Safari property
  const isPWA = (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
                window.matchMedia('(display-mode: standalone)').matches;
  
  // Feature detection instead of device-specific checks
  const safeAreaSupport = CSS.supports('padding-top: env(safe-area-inset-top)');
  const hasNotch = safeAreaSupport && isIOS;
  
  // Dynamic Island detection (iPhone 14 Pro and later)
  // Use feature detection rather than specific device targeting
  const hasDynamicIsland = isIPhone && height >= 852 && safeAreaSupport;
  
  // Home indicator detection (iPhone X and later)
  const hasHomeIndicator = isIPhone && height >= 812 && safeAreaSupport;

  // iPhone Fold, closed: the cover screen is much squarer than any other
  // iPhone (~0.69 vs ~0.46 width:height) and iOS draws its status bar in a
  // strip down the right edge without reporting a safe-area inset for it.
  // Unfolded it crosses the 938px desktop breakpoint, so the phone header
  // (the only thing that reads this) isn't shown there.
  const isFoldCover = isIPhone && Math.min(width, height) / Math.max(width, height) > 0.55;

  // Device class based on screen size (for responsive design)
  let deviceClass: 'compact' | 'regular' | 'large' = 'regular';
  if (width <= 375) {
    deviceClass = 'compact';
  } else if (width >= 428) {
    deviceClass = 'large';
  }
  
  // Determine primary device type
  let type: DeviceInfo['type'] = 'unknown';
  if (isIPhone) {
    type = 'iphone';
  } else if (isIPad) {
    type = 'ipad';
  } else if (isAndroid) {
    type = 'android';
  } else if (!isIOS && !isAndroid) {
    type = 'desktop';
  }
  
  return {
    type,
    isIOS,
    isPWA,
    hasNotch,
    hasDynamicIsland,
    hasHomeIndicator,
    isFoldCover,
    screenSize: { width, height },
    deviceClass,
    safeAreaSupport
  };
}

/**
 * Gets current safe area insets
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  const computedStyle = getComputedStyle(document.documentElement);
  
  const parseInset = (value: string): number => {
    const parsed = parseInt(value.replace('px', ''), 10);
    return isNaN(parsed) ? 0 : parsed;
  };
  
  return {
    top: parseInset(computedStyle.getPropertyValue('--safe-area-inset-top') || '0'),
    bottom: parseInset(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0'),
    left: parseInset(computedStyle.getPropertyValue('--safe-area-inset-left') || '0'),
    right: parseInset(computedStyle.getPropertyValue('--safe-area-inset-right') || '0')
  };
}

/**
 * Applies device-specific CSS classes to the document
 */
export function applyDeviceClasses(deviceInfo: DeviceInfo): void {
  const { documentElement } = document;
  
  // Clear existing device classes
  const existingClasses = Array.from(documentElement.classList).filter(cls => 
    cls.startsWith('device-') || cls.startsWith('ios-') || cls.startsWith('iphone-')
  );
  documentElement.classList.remove(...existingClasses);
  
  // Apply new classes based on device info
  documentElement.classList.add(`device-${deviceInfo.type}`);
  documentElement.classList.add(`device-class-${deviceInfo.deviceClass}`);
  
  if (deviceInfo.isIOS) {
    documentElement.classList.add('ios-device');
  }
  
  if (deviceInfo.isPWA) {
    documentElement.classList.add('pwa-mode');
  }
  
  if (deviceInfo.hasNotch) {
    documentElement.classList.add('has-notch');
  }
  
  if (deviceInfo.hasDynamicIsland) {
    documentElement.classList.add('has-dynamic-island');
  }
  
  if (deviceInfo.hasHomeIndicator) {
    documentElement.classList.add('has-home-indicator');
  }

  if (deviceInfo.isFoldCover) {
    documentElement.classList.add('iphone-fold-cover');
  }

  if (deviceInfo.safeAreaSupport) {
    documentElement.classList.add('safe-area-supported');
  }
}

/**
 * Sets up CSS custom properties for safe areas
 */
export function setupSafeAreaProperties(): void {
  const style = document.documentElement.style;
  
  // Set up CSS custom properties that mirror env() values
  style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top, 0px)');
  style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom, 0px)');
  style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left, 0px)');
  style.setProperty('--safe-area-inset-right', 'env(safe-area-inset-right, 0px)');
}

/**
 * Initialize device detection and apply classes
 */
export function initializeDeviceDetection(): DeviceInfo {
  const deviceInfo = getDeviceInfo();

  // Set up safe area properties first
  setupSafeAreaProperties();

  // Apply device classes
  applyDeviceClasses(deviceInfo);

  // Log device info for debugging (helpful for troubleshooting)
  return deviceInfo;
}

/**
 * React hook for device detection
 */
export function useDeviceInfo(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>(() => getDeviceInfo());

  React.useEffect(() => {
    const handleResize = () => {
      const newDeviceInfo = getDeviceInfo();
      setDeviceInfo(newDeviceInfo);
      applyDeviceClasses(newDeviceInfo);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return deviceInfo;
}
